import type {
	BabyBjornCart,
	BabyBjornOrderType,
} from "@babybjorn/utils/commercetools/custom-types/babybjorn-order";
import type {
	AddressDraft,
	Cart,
	CartAddDiscountCodeAction,
	CartDraft,
	CartRemoveDiscountCodeAction,
	CartSetCountryAction,
	CartSetCustomFieldAction,
	CartUpdate,
	CustomerSetCustomTypeAction,
	CustomFieldsDraft,
	OrderFromCartDraft,
	ShippingMethod,
} from "@commercetools/platform-sdk";
import { addBreadcrumb } from "@sentry/gatsby";
import PQueue from "p-queue";
import type { WritableDeep } from "type-fest";
import type { State } from "./fetch-from-ct";
import { fetchFromCTWithSession } from "./frontend-session";
import type { ExpandedOrder } from "./order-service";

const projectKey = process.env.GATSBY_COMMERCETOOLS_PROJECT_KEY;
const defaultExpandParameters = `?expand=discountCodes[0].discountCode&expand=directDiscounts[*]&expand=paymentInfo.payments[*]&expand=lineItems[*].productType&expand=shippingInfo.shippingMethod&expand=custom.type`;

const logCartService = (message: string, data: object) => {
	// For Sentry.
	addBreadcrumb({
		category: "cart-service",
		message: message,
		level: "info",
		data: {
			cartDraft: data,
		},
	});
};

let cartVersion: number;
const cartRequestQueue = new PQueue({ concurrency: 1 });
cartRequestQueue.on("completed", (result: State<BabyBjornCart>) => {
	if (result.data?.version) {
		cartVersion = result.data.version;
	}
});

let firstRequest = true;
const fetchCartFromCt = async <T extends BabyBjornCart | ExpandedOrder>(
	path: string,
	body?: WritableDeep<CartUpdate | CartDraft | OrderFromCartDraft>,
	fetchLatestVersion = true,
) => {
	const requestJob = await cartRequestQueue.add(
		async () => {
			if (firstRequest && fetchLatestVersion) {
				firstRequest = false;
				try {
					const cart = await fetchFromCTWithSession<BabyBjornCart>(path);
					if (cart.data?.version) {
						cartVersion = cart.data.version;
					}
				} finally {
					firstRequest = true;
				}
			}
			if (cartVersion && body && "version" in body) {
				body.version = cartVersion;
			}

			return await fetchFromCTWithSession<T>(path, body);
		},
		{ throwOnTimeout: true },
	);

	return requestJob;
};

export const createCartFromDraft = async (
	cartDraft: CartDraft,
): Promise<BabyBjornCart> => {
	logCartService("createCartFromDraft", cartDraft);
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/in-store/key=${process.env.GATSBY_STORE}/me/carts`,
		{
			...cartDraft,
			locale: process.env.GATSBY_LANGUAGE,
		},
		false,
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to create a cart.");
	}

	return res.data;
};

let fetchCartByIdPromise: undefined | Promise<State<BabyBjornCart>>;
export const fetchCartById = async (cartId: string): Promise<BabyBjornCart> => {
	logCartService("fetchCartById", { cartId: cartId });
	let res;
	try {
		if (!fetchCartByIdPromise) {
			fetchCartByIdPromise = fetchCartFromCt<BabyBjornCart>(
				`/${projectKey}/me/carts/${cartId}${defaultExpandParameters}`,
			);
		}

		res = await fetchCartByIdPromise;
	} finally {
		fetchCartByIdPromise = undefined;
	}

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to get a cart.");
	}

	return res.data;
};

export const addAddresses = async (
	cart: Cart,
	billingAddress?: AddressDraft,
	shippingAddress?: AddressDraft,
): Promise<BabyBjornCart> => {
	logCartService("addAddresses", { cartId: cart.id });
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "setBillingAddress",
					address: billingAddress,
				},
				{
					action: "setShippingAddress",
					address: shippingAddress ?? billingAddress,
				},
				{
					action: "setCustomerEmail",
					email:
						billingAddress?.email?.trim() ||
						shippingAddress?.email?.trim() ||
						"",
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to set addresses on cart.");
	}
	return res.data;
};

export const recalculateCart = async (
	cart: Cart,
	updateProductData?: boolean,
): Promise<BabyBjornCart> => {
	logCartService("recalculateCart", { cartId: cart.id });
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}&setShipping=true&recalculate=true`,
		{
			version: cart.version,
			actions: [
				{
					action: "recalculate",
					updateProductData: updateProductData ?? false,
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to recalculate cart.");
	}

	return res.data;
};

export const addPayment = async (
	cart: Cart,
	paymentId: string,
): Promise<BabyBjornCart> => {
	logCartService("addPayment", { cartId: cart.id, paymentId: paymentId });
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "addPayment",
					payment: {
						typeId: "payment",
						id: paymentId,
					},
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to add payment to cart.");
	}

	return res.data;
};

export const removePayment = async (
	cart: Cart,
	paymentId: string,
): Promise<BabyBjornCart> => {
	logCartService("removePayment", { cartId: cart.id, paymentId });
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "removePayment",
					payment: {
						typeId: "payment",
						id: paymentId,
					},
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to remove payment to cart.");
	}

	return res.data;
};

export const changeLineItemQuantity = async (
	cart: Cart,
	lineItemId: string,
	quantity: number,
): Promise<BabyBjornCart> => {
	logCartService("changeLineItemQuantity", {
		cartId: cart.id,
		lineItemId,
		quantity,
	});

	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "changeLineItemQuantity",
					lineItemId,
					quantity,
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to set quantity on cart.");
	}

	return res.data;
};

export interface AddLineItemProps {
	productId: string;
	variantId: number;
	supplyChannel?: string | null;
	custom?: CustomFieldsDraft;
	quantity?: number;
}

export const addLineItems = async (
	cart: Cart,
	items: AddLineItemProps[],
): Promise<BabyBjornCart> => {
	for (const item of items) {
		logCartService("addLineItem", {
			cartId: cart.id,
			productId: item.productId,
			variantId: item.variantId,
			supplyChannel: item.supplyChannel,
			custom: item.custom,
			quantity: item.quantity,
		});
	}

	const lineItemActions = items.map((item) => ({
		action: "addLineItem" as const,
		productId: item.productId,
		variantId: item.variantId,
		supplyChannel: item.supplyChannel
			? { typeId: "channel" as const, id: item.supplyChannel }
			: undefined,
		quantity: item.quantity,
		custom: item.custom,
	}));

	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: lineItemActions,
		},
	);

	if (res.error) {
		console.warn(res.error);
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to add line item to cart.");
	}

	return res.data;
};

export const updateGiftMessage = async (
	cart: Cart,
	lineItemId: string,
	message: string,
): Promise<BabyBjornCart> => {
	logCartService("updateGiftMessage", {
		cartId: cart.id,
		lineItemId,
		message,
	});

	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "setLineItemCustomField",
					lineItemId,
					name: "message",
					value: message,
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to change gift message.");
	}

	return res.data;
};

export const removeLineItemById = async (
	cart: Cart,
	lineItemId: string,
): Promise<BabyBjornCart> => {
	logCartService("removeLineItemById", {
		cartId: cart.id,
		lineItemId,
	});
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "removeLineItem",
					lineItemId: lineItemId,
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to remove line item from cart.");
	}

	return res.data;
};

export const removeLineItemByVariant = async (
	cart: Cart,
	productId: string,
	variantId: number,
): Promise<BabyBjornCart> => {
	logCartService("removeLineItemByVariant", {
		cartId: cart.id,
		productId,
		variantId,
	});
	const lineItem = cart.lineItems.find(
		(lineItem) =>
			lineItem.variant.id === variantId && lineItem.productId === productId,
	);

	if (!lineItem) {
		throw new Error(`Line item not found for variant with id: ${variantId}`);
	}

	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [
				{
					action: "removeLineItem",
					lineItemId: lineItem.id,
				},
			],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to remove line item from cart.");
	}

	return res.data;
};

export const removeLineItemsById = async (
	cart: Cart,
	lineItemIds: string[],
): Promise<BabyBjornCart> => {
	logCartService("removeLineItemsById", {
		cartId: cart.id,
		lineItemIds,
	});

	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: lineItemIds.map((lineItemId) => ({
				action: "removeLineItem",
				lineItemId: lineItemId,
			})),
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to remove line items from cart.");
	}

	return res.data;
};

export const addCustomField = async (
	cart: Cart,
	customFieldDraft: Omit<CartSetCustomFieldAction, "action">,
): Promise<BabyBjornCart> => {
	logCartService("addCustomField", {
		cartId: cart.id,
		customFieldDraft,
	});

	const setCustomFieldAction: CartSetCustomFieldAction = {
		...customFieldDraft,
		action: "setCustomField",
	};

	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [setCustomFieldAction],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to add custom field to cart.");
	}

	return res.data;
};

export const addDiscountCode = async (
	cart: Cart,
	code: string,
): Promise<BabyBjornCart> => {
	logCartService("addDiscountCode", {
		cartId: cart.id,
		code,
	});

	const addDiscountCodeAction: CartAddDiscountCodeAction = {
		action: "addDiscountCode",
		code: code,
	};
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [addDiscountCodeAction],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to add discount code to cart.");
	}

	return res.data;
};

export const removeDiscountCode = async (
	cart: Cart,
): Promise<BabyBjornCart> => {
	logCartService("removeDiscountCode", {
		cartId: cart.id,
	});
	const firstDiscountReference = cart.discountCodes[0]?.discountCode.id;
	if (!firstDiscountReference) {
		throw new Error("No discount code in cart");
	}
	const removeDiscountCodeAction: CartRemoveDiscountCodeAction = {
		action: "removeDiscountCode",
		discountCode: { id: firstDiscountReference, typeId: "discount-code" },
	};
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [removeDiscountCodeAction],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to remove discount code from cart.");
	}

	return res.data;
};

export const setCartCustomFields = async (
	cartId: string,
	customFields: Partial<Record<keyof BabyBjornOrderType["fields"], unknown>>,
): Promise<BabyBjornCart> => {
	logCartService("setCartCustomFields", {
		cartId: cartId,
		customFields,
	});

	const currentCartResponse = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cartId}${defaultExpandParameters}`,
	);

	const currentCart = currentCartResponse.data;

	if (!currentCart) {
		throw new Error("Failed to get current cart.");
	}

	const newValues = (
		Object.entries(customFields) as [
			keyof BabyBjornOrderType["fields"],
			unknown,
		][]
	).filter(([key, value]) => {
		if (currentCart.custom.fields[key] === value) {
			console.info(`Cart custom field ${key} already set to ${String(value)}.`);
			return false;
		}

		return true;
	});

	if (newValues.length === 0) {
		console.info("No new custom fields to set.");
		return currentCart;
	}

	const action: CustomerSetCustomTypeAction = {
		action: "setCustomType",
		type: {
			key: "babybjorn-order",
			typeId: "type",
		},
		fields: {
			...currentCart.custom.fields,
			...Object.fromEntries(newValues),
		},
	};
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${currentCart.id}${defaultExpandParameters}`,
		{
			version: currentCart.version,
			actions: [action],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error(`Failed to set custom fields to cart.`);
	}

	return res.data;
};

export const setCartCustomField = async (
	cartId: string,
	key: keyof BabyBjornOrderType["fields"],
	value: unknown,
): Promise<BabyBjornCart> =>
	setCartCustomFields(cartId, {
		[key]: value,
	});

export const setShippingMethod = async (
	cart: BabyBjornCart,
	shippingMethod?: ShippingMethod,
	shippingMethodId?: string,
): Promise<BabyBjornCart> => {
	logCartService("setShippingMethod", {
		cartId: cart.id,
		shippingMethod,
		shippingMethodId,
	});

	if (!shippingMethod && !shippingMethodId) {
		throw new Error("No shipping method provided.");
	}

	const cartHasOnlyGiftCards = cart.lineItems.every(
		(lineItem) => lineItem.productType.obj?.key === "gift-card",
	);

	if (cartHasOnlyGiftCards) {
		throw new Error("Cart has only gift cards, cant set shipping method.");
	}

	const upsStores = ["us", "ca"];
	const isAuStore = cart.store?.key === "au";
	const isUpsStore = upsStores.includes(cart.store?.key ?? "");
	const isFlatrateShippingStore = !isAuStore && !isUpsStore;

	let res;
	const id = shippingMethodId ?? shippingMethod?.id;

	if (isFlatrateShippingStore) {
		res = await fetchCartFromCt<BabyBjornCart>(
			`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}&setShipping=true`,
			{
				version: cart.version,
				actions: [
					{
						action: "setShippingMethod",
						shippingMethod: {
							typeId: "shipping-method",
							id,
						},
					},
				],
			},
		);
	} else if (isUpsStore) {
		if (cart.custom.fields["selectedShippingMethod"] === id) {
			console.info(
				`Cart custom field selectedShippingMethod already set to ${id}.`,
			);
			return cart;
		}

		const action: CustomerSetCustomTypeAction = {
			action: "setCustomType",
			type: {
				key: "babybjorn-order",
				typeId: "type",
			},
			fields: {
				...cart.custom.fields,
				selectedShippingMethod: id,
			},
		};
		res = await fetchCartFromCt<BabyBjornCart>(
			`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
			{
				version: cart.version,
				actions: [action],
			},
		);
	} else if (isAuStore) {
		console.warn(
			`Trying to set shipping. Frontend should not set shipping for Australia Post (id: ${id})`,
		);
	}

	if (!res) throw new Error("Failed to set shipping method on cart.");

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to set shipping method on cart.");
	}

	return res.data;
};

export const createOrderFromCart = async (
	cart: Cart,
): Promise<ExpandedOrder> => {
	logCartService("createOrderFromCart", {
		cartId: cart.id,
	});

	const updatedCart = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
	);

	if (updatedCart.error) {
		throw updatedCart.error;
	}

	if (!updatedCart.data) {
		throw new Error("Failed to get cart.");
	}

	const res = await fetchFromCTWithSession<ExpandedOrder>(
		`/${projectKey}/me/orders${defaultExpandParameters}`,
		{
			id: updatedCart.data.id,
			version: updatedCart.data.version,
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to create order.");
	}

	return res.data;
};

export const setCountry = async (
	cart: Cart,
	country: string,
): Promise<BabyBjornCart> => {
	logCartService("setCountry", {
		cartId: cart.id,
		country,
	});

	const setCountryAction: CartSetCountryAction = {
		action: "setCountry",
		country: country,
	};
	const res = await fetchCartFromCt<BabyBjornCart>(
		`/${projectKey}/me/carts/${cart.id}${defaultExpandParameters}`,
		{
			version: cart.version,
			actions: [setCountryAction],
		},
	);

	if (res.error) {
		throw res.error;
	}

	if (!res.data) {
		throw new Error("Failed to set country on cart.");
	}

	return res.data;
};
